import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import souvenirService from '../../services/souvenir.service'
import LoadingSpinner from '../../components/LoadingSpinner'
import { EP_STATUS_LABELS } from '../../constants/easyparcel-status'
import {
  TruckIcon,
  DocumentArrowDownIcon,
  XCircleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'

interface VendorOrder {
  id: string
  orderNumber: string
  status: string
  paymentStatus: string
  totalAmount: number
  deliveryMethod: 'DELIVERY' | 'PICKUP'
  courierName?: string
  serviceName?: string
  awbNumber?: string
  awbUrl?: string
  trackingUrl?: string
  epStatus?: string
  createdAt: string
  user: {
    firstName: string
    lastName: string
    email: string
  }
  items: Array<{
    name: string
    quantity: number
  }>
}

// Vendor status flow (mirrors server validTransitions)
const NEXT_STATUS: Record<string, string | null> = {
  PENDING: 'CONFIRMED',
  CONFIRMED: 'PREPARING',
  PREPARING: 'READY',
  READY: null, // handled specially: DELIVERY → ship, PICKUP → COMPLETED
  SHIPPED: 'COMPLETED',
  COMPLETED: null,
  CANCELLED: null,
}

const CANCELLABLE = new Set(['PENDING', 'CONFIRMED', 'PREPARING'])

const VendorOrdersPage: React.FC = () => {
  const { t } = useTranslation()
  const [orders, setOrders] = useState<VendorOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    fetchOrders()
  }, [])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const response = await souvenirService.getVendorOrders()
      const list = Array.isArray(response.data) ? response.data : response.data?.data || []
      setOrders(list)
    } catch (err: any) {
      console.error('Error fetching orders:', err)
      setError(err.response?.data?.message || 'Failed to fetch orders')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-800'
      case 'PREPARING':
        return 'bg-purple-100 text-purple-800'
      case 'READY':
        return 'bg-indigo-100 text-indigo-800'
      case 'SHIPPED':
        return 'bg-cyan-100 text-cyan-800'
      case 'COMPLETED':
        return 'bg-green-100 text-green-800'
      case 'CANCELLED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleStatusUpdate = async (order: VendorOrder, status: string) => {
    setBusyId(order.id)
    try {
      await souvenirService.updateOrderStatus(order.id, status)
      toast.success(
        `${order.orderNumber}: ${t(`orders.status.${status.toLowerCase()}`, status)}`,
      )
      await fetchOrders()
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('vendor.ship.statusFailed'))
    } finally {
      setBusyId(null)
    }
  }

  const handleCancel = async (order: VendorOrder) => {
    if (!window.confirm(t('vendor.ship.cancelConfirm', { orderNumber: order.orderNumber }))) return
    await handleStatusUpdate(order, 'CANCELLED')
  }

  const handleShip = async (order: VendorOrder) => {
    if (
      !window.confirm(
        t('vendor.ship.shipConfirm', {
          orderNumber: order.orderNumber,
          courier: order.courierName || '',
        }),
      )
    ) {
      return
    }
    setBusyId(order.id)
    try {
      const res = await souvenirService.shipOrder(order.id)
      const shipped = res.data?.data
      toast.success(
        shipped?.awbNumber
          ? t('vendor.ship.shippedWithAwb', { awb: shipped.awbNumber })
          : t('vendor.ship.shipped'),
      )
      await fetchOrders()
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('vendor.ship.shipFailed'))
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Error: {error}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('vendor.ship.title')}</h1>
          <p className="text-gray-600 mt-1">{t('vendor.ship.subtitle')}</p>
        </div>
        <button
          onClick={fetchOrders}
          className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          title={t('vendor.ship.refresh')}
        >
          <ArrowPathIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Orders List */}
      {orders.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <svg className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('vendor.ship.noOrders')}</h3>
          <p className="text-gray-500">{t('vendor.ship.noOrdersHint')}</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('vendor.ship.orderNumber')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('vendor.ship.customer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('vendor.ship.items')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('vendor.ship.total')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('vendor.ship.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('vendor.ship.date')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('vendor.ship.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => {
                const busy = busyId === order.id
                const nextStatus = NEXT_STATUS[order.status]
                return (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{order.orderNumber}</div>
                      <div className="text-xs text-gray-500">
                        {order.deliveryMethod === 'PICKUP'
                          ? t('checkout.pickup')
                          : order.courierName
                            ? `${order.courierName}${order.serviceName ? ` · ${order.serviceName}` : ''}`
                            : t('checkout.delivery')}
                      </div>
                      {order.paymentStatus === 'PAID' ? (
                        <span className="inline-block mt-1 px-2 text-[11px] font-semibold rounded-full bg-green-50 text-green-700">
                          {t('orders.paid')}
                        </span>
                      ) : (
                        <span className="inline-block mt-1 px-2 text-[11px] font-semibold rounded-full bg-yellow-50 text-yellow-700">
                          {t('orders.unpaid')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {order.user.firstName} {order.user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{order.user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.items.length} {t('orders.items')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      RM{Number(order.totalAmount).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                        {t(`orders.status.${order.status.toLowerCase()}`, order.status)}
                      </span>
                      {order.status === 'SHIPPED' && order.epStatus && (
                        <span className="mt-1 block px-2 w-fit text-[11px] font-semibold rounded-full bg-gray-100 text-gray-700">
                          {EP_STATUS_LABELS[order.epStatus] || `EP ${order.epStatus}`}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {/* Advance status */}
                        {nextStatus && order.status !== 'READY' && (
                          <button
                            onClick={() => handleStatusUpdate(order, nextStatus)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-primary-600 text-primary-600 rounded-md text-xs font-medium hover:bg-primary-50 disabled:opacity-50"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            {t(`vendor.ship.action.${nextStatus.toLowerCase()}`)}
                          </button>
                        )}

                        {/* READY: ship via courier (delivery) or complete (pickup) */}
                        {order.status === 'READY' && order.deliveryMethod === 'DELIVERY' && (
                          <button
                            onClick={() => handleShip(order)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-md text-xs font-medium hover:bg-primary-700 disabled:opacity-50"
                          >
                            <TruckIcon className={`h-4 w-4 ${busy ? 'animate-pulse' : ''}`} />
                            {t('vendor.ship.ship')}
                          </button>
                        )}
                        {order.status === 'READY' && order.deliveryMethod === 'PICKUP' && (
                          <button
                            onClick={() => handleStatusUpdate(order, 'COMPLETED')}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-md text-xs font-medium hover:bg-primary-700 disabled:opacity-50"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            {t('vendor.ship.action.completed')}
                          </button>
                        )}

                        {/* SHIPPED: label + tracking + manual complete fallback */}
                        {order.status === 'SHIPPED' && order.awbUrl && (
                          <a
                            href={order.awbUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-primary-600 text-primary-600 rounded-md text-xs font-medium hover:bg-primary-50"
                          >
                            <DocumentArrowDownIcon className="h-4 w-4" />
                            {t('orders.viewLabel')}
                          </a>
                        )}
                        {order.status === 'SHIPPED' && order.trackingUrl && (
                          <a
                            href={order.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-600 rounded-md text-xs font-medium hover:bg-gray-50"
                          >
                            {t('orders.courierSite')}
                          </a>
                        )}

                        {/* Cancel */}
                        {CANCELLABLE.has(order.status) && (
                          <button
                            onClick={() => handleCancel(order)}
                            disabled={busy}
                            className="inline-flex items-center p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md disabled:opacity-50"
                            title={t('vendor.ship.cancel')}
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default VendorOrdersPage
